import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles, shorthands, Button, Spinner, Combobox, Option, mergeClasses, Tooltip } from "@fluentui/react-components";
import { ArrowSync16Regular } from "@fluentui/react-icons";
import FileUploadPanel from "./FileUploadPanel";
import FileResourceOption from "./FileResourceOption";
import { MAX_SELECTED_FILES } from "../utils/resourceUtils";
import { useComboboxStyles, comboboxListboxProps, comboboxPositioning, UPLOAD_FOLDER_LISTBOX_CLASS } from "../styles/comboboxStyles";
import { useActionButtonStyles } from "../styles/actionButtonStyles";

const FILE_SELECTOR_TOOLTIP =
  "Choose which documents Unicli should use to answer your request. If none are selected, Unicli automatically chooses the most relevant information from all available documents.";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    alignSelf: "stretch",
    width: "100%",
    ...shorthands.gap("6px"),
    ...shorthands.padding("0px", "0px", "4px"),
  },
  searchRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("8px"),
    width: "100%",
  },
  comboboxFlex: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    width: "100%",
    display: "flex",
  },
  statusLine: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("8px"),
    fontFamily: "Segoe UI",
    fontSize: "13px",
    lineHeight: "18px",
    color: "#333333",
  },
  statusError: {
    color: "#C50F1F",
  },
  statusWarning: {
    color: "#8A4B00",
  },
  helperText: {
    fontFamily: "Segoe UI",
    fontSize: "13px",
    color: "#616161",
  },
});

const FileContextPicker = ({
  resourceSearchTerm,
  setResourceSearchTerm,
  filteredResources,
  selectedFiles,
  selectFile,
  resourceLoading,
  resourceError,
  validationMessage,
  loadResources,
  disabled,
  isResourceListEmpty,
  propertyId,
  fileUpload,
  onViewResource,
}) => {
  const styles = useStyles();
  const comboboxStyles = useComboboxStyles();
  const actionStyles = useActionButtonStyles();
  const uploadPanelRef = useRef(null);
  const fileComboboxRef = useRef(null);

  const atMax = selectedFiles.length >= MAX_SELECTED_FILES;
  const maxFilesLabel = `Maximum ${MAX_SELECTED_FILES} files selected`;
  const showUploadButton = propertyId > 0 && fileUpload;
  const isRefreshDisabled = disabled || resourceLoading;

  useEffect(() => {
    if (!fileUpload?.isPanelOpen) {
      return;
    }
    const dismissPanel = fileUpload.dismissPanel;

    const swallowOutsidePointer = (event) => {
      if (uploadPanelRef.current?.contains(event.target)) {
        return;
      }
      if (event.target.closest?.(`.${UPLOAD_FOLDER_LISTBOX_CLASS}`)) {
        return;
      }
      if (fileComboboxRef.current?.contains(event.target)) {
        dismissPanel();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      dismissPanel();
      event.preventDefault();
      event.stopPropagation();
    };

    const captureOptions = { capture: true };
    document.addEventListener("mousedown", swallowOutsidePointer, captureOptions);
    document.addEventListener("click", swallowOutsidePointer, captureOptions);
    return () => {
      document.removeEventListener("mousedown", swallowOutsidePointer, captureOptions);
      document.removeEventListener("click", swallowOutsidePointer, captureOptions);
    };
  }, [fileUpload?.isPanelOpen, fileUpload?.dismissPanel]);

  const handleRefresh = () => {
    if (isRefreshDisabled) {
      return;
    }
    loadResources().catch(() => {});
  };

  const handleInput = (event) => {
    setResourceSearchTerm(event.target.value);
  };

  const handleOptionSelect = (_, data) => {
    const resource = filteredResources.find((item) => item.connected_id === data.optionValue);
    if (resource && !atMax && !disabled) {
      selectFile(resource);
    }
  };

  const showStatusLine = resourceError || validationMessage || isResourceListEmpty;

  return (
    <div className={styles.container}>
      <div className={styles.searchRow}>
        <div ref={fileComboboxRef} className={styles.comboboxFlex}>
          <Tooltip
            content={FILE_SELECTOR_TOOLTIP}
            relationship="description"
            withArrow
            showDelay={300}
            positioning="above"
          >
            <div className={styles.comboboxFlex}>
              <Combobox
                className={mergeClasses(comboboxStyles.dropdownMenuCompose, styles.comboboxFlex)}
                style={{ flex: 1 }}
                placeholder={atMax ? maxFilesLabel : "Use specific files..."}
                value={resourceSearchTerm}
                onInput={handleInput}
                onChange={(event) => setResourceSearchTerm(event.target.value)}
                onOptionSelect={handleOptionSelect}
                disabled={disabled || atMax}
                freeform
                selectedOptions={[]}
                positioning={comboboxPositioning}
                listbox={comboboxListboxProps(comboboxStyles.dropdownMenuListbox)}
                input={{ className: comboboxStyles.comboBoxInput, autoComplete: "off" }}
                size="small"
              >
                {filteredResources.map((resource) => (
                  <Option
                    key={resource.connected_id}
                    value={resource.connected_id}
                    text={resource.name}
                  >
                    <FileResourceOption resource={resource} onViewResource={onViewResource} />
                  </Option>
                ))}
              </Combobox>
            </div>
          </Tooltip>
        </div>
        <Button
          className={actionStyles.iconActionButton}
          appearance="outline"
          icon={resourceLoading ? <Spinner size="tiny" /> : <ArrowSync16Regular />}
          aria-label="Refresh file list"
          title="Refresh file list"
          onClick={handleRefresh}
          disabled={isRefreshDisabled}
        />
        {showUploadButton && (
          <Button
            className={actionStyles.draftButton}
            appearance="transparent"
            style={{ minWidth: "unset" }}
            onClick={fileUpload.openPanel}
            disabled={disabled || fileUpload.uploading}
          >
            <span className={actionStyles.draftButtonText}>Upload</span>
          </Button>
        )}
      </div>

      {fileUpload?.isPanelOpen && (
        <div ref={uploadPanelRef}>
          <FileUploadPanel
            folders={fileUpload.folders}
            selectedFolderId={fileUpload.selectedFolderId}
            setSelectedFolderId={fileUpload.setSelectedFolderId}
            stagedFiles={fileUpload.stagedFiles}
            folderLoading={fileUpload.folderLoading}
            folderError={fileUpload.folderError}
            uploading={fileUpload.uploading}
            uploadError={fileUpload.uploadError}
            validation={fileUpload.validation}
            canSubmit={fileUpload.canSubmit}
            onAddFiles={fileUpload.addFiles}
            onRenameFile={fileUpload.renameFile}
            onRemoveStagedFile={fileUpload.removeStagedFile}
            onSubmit={fileUpload.submitUpload}
            onCancel={fileUpload.cancelPanel}
            onRetryFolders={fileUpload.loadFolders}
          />
        </div>
      )}

      {atMax && <div className={styles.helperText}>{maxFilesLabel}</div>}

      {showStatusLine && (
        <div
          className={mergeClasses(
            styles.statusLine,
            (resourceError || (validationMessage && validationMessage.includes("not linked"))) &&
              styles.statusError,
            validationMessage &&
              !resourceError &&
              !(validationMessage && validationMessage.includes("not linked")) &&
              styles.statusWarning
          )}
        >
          {!resourceLoading && resourceError && (
            <>
              <span>{resourceError}</span>
              <Button size="small" appearance="outline" onClick={() => loadResources()}>
                Try again
              </Button>
            </>
          )}
          {!resourceLoading && !resourceError && validationMessage && <span>{validationMessage}</span>}
          {!resourceLoading &&
            !resourceError &&
            !validationMessage &&
            isResourceListEmpty && (
              <span className={styles.helperText}>No files available for this property.</span>
            )}
        </div>
      )}
    </div>
  );
};

FileContextPicker.propTypes = {
  resourceSearchTerm: PropTypes.string.isRequired,
  setResourceSearchTerm: PropTypes.func.isRequired,
  filteredResources: PropTypes.array.isRequired,
  selectedFiles: PropTypes.array.isRequired,
  selectFile: PropTypes.func.isRequired,
  resourceLoading: PropTypes.bool.isRequired,
  resourceError: PropTypes.string,
  validationMessage: PropTypes.string,
  loadResources: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isResourceListEmpty: PropTypes.bool,
  propertyId: PropTypes.number,
  fileUpload: PropTypes.object,
  onViewResource: PropTypes.func,
};

export default FileContextPicker;
